// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Portal from '../portal';
import useDragAndDropReorder from './use-drag-and-drop-reorder';
import useLiveAnnouncements from './use-live-announcements';
import styles from './styles.css.js';
export function DndContainer({ getId, sortedOptions, onChange, disabled, renderOption, renderActiveOption, i18nStrings, dragOverlayClassName = styles['drag-overlay'], children = content => content, }) {
    const { activeItem, collisionDetection, handleKeyDown, sensors, setActiveItem } = useDragAndDropReorder({
        sortedOptions,
        getId,
    });
    const activeOption = activeItem ? sortedOptions.find(option => getId(option) === activeItem) : null;
    const isDragging = activeItem !== null;
    const announcements = useLiveAnnouncements(Object.assign({ sortedOptions, getId, isDragging }, i18nStrings));
    return (React.createElement(DndContext, { sensors: sensors, collisionDetection: collisionDetection, accessibility: {
            announcements,
            restoreFocus: false,
            screenReaderInstructions: i18nStrings.dragHandleAriaDescription
                ? { draggable: i18nStrings.dragHandleAriaDescription }
                : undefined,
        }, onDragStart: ({ active }) => setActiveItem(active.id), onDragEnd: event => {
            setActiveItem(null);
            const { active, over } = event;
            if (over && active.id !== over.id) {
                const oldIndex = sortedOptions.findIndex(option => getId(option) === active.id);
                const newIndex = sortedOptions.findIndex(option => getId(option) === over.id);
                onChange(arrayMove([...sortedOptions], oldIndex, newIndex));
            }
        }, onDragCancel: () => setActiveItem(null) },
        children(React.createElement(SortableContext, { disabled: disabled, items: sortedOptions.map(option => getId(option)), strategy: verticalListSortingStrategy }, sortedOptions.map(option => (React.createElement(DraggableOption, { key: getId(option), option: option, getId: getId, renderOption: renderOption, onKeyDown: handleKeyDown, dragHandleAriaLabel: i18nStrings.dragHandleAriaLabel }))))),
        React.createElement(Portal, null,
            React.createElement(DragOverlay, { className: dragOverlayClassName, dropAnimation: null, style: { zIndex: 5000 } }, activeOption &&
                renderActiveOption({
                    option: activeOption,
                    dragHandleAriaLabel: i18nStrings.dragHandleAriaLabel,
                    listeners: { onKeyDown: handleKeyDown },
                })))));
}
function DraggableOption({ option, getId, dragHandleAriaLabel, onKeyDown, renderOption, }) {
    const { isDragging, isSorting, listeners, setNodeRef, transform, attributes } = useSortable({ id: getId(option) });
    const style = {
        transform: CSS.Translate.toString(transform),
    };
    const combinedListeners = Object.assign(Object.assign({}, listeners), { onKeyDown: (event) => {
            if (onKeyDown) {
                onKeyDown(event);
            }
            if (listeners === null || listeners === void 0 ? void 0 : listeners.onKeyDown) {
                listeners.onKeyDown(event);
            }
        } });
    return (React.createElement(React.Fragment, null, renderOption({
        option,
        dragHandleAriaLabel,
        ref: setNodeRef,
        style,
        isDragging,
        isSorting,
        listeners: combinedListeners,
        attributes,
    })));
}
//# sourceMappingURL=index.js.map